<template>
    <div class="rsvp-list-container">
        <h1 class="list-title">Danh sách Khách</h1>
        <p v-if="loading" class="loading-message">
            <ProgressSpinner />
        </p>
        <p v-else-if="error" class="error-message">Đã xảy ra lỗi khi tải dữ liệu: {{ error }}</p>
        <p v-else-if="allRsvps.length === 0" class="no-data-message">Chưa có khách RSVP nào.</p>
        <div v-else class="rsvp-cards-grid">
            <div class="text-6xl text-red-400">
                <span>👨‍🦳</span>{{ attendingGuests.adultGuestsCount }}
                <span>👶</span>{{ attendingGuests.childGuestsCount }}
            </div>
            <div v-for="rsvp in allRsvps" :key="rsvp.id" class="rsvp-card flex flex-column">
                <h3 class="card-name">{{ rsvp.name }}</h3>
                <p class="card-status" :class="{'attending': isAttending(rsvp), 'not-attending': isAttending(rsvp)}">
                    {{ isAttending(rsvp) ? 'Sẽ tham dự' : 'Không thể tham dự' }}
                </p>
                <p v-if="isAttending(rsvp)" class="card-guests">
                    Số khách: {{ rsvp.adultGuestsCount || 0 }} người lớn, {{ rsvp.childGuestsCount || 0 }} trẻ em
                </p>
                <div class="card-details">
                    <p class="note">{{rsvp.note}}</p>
                    <p class="entertainment">{{rsvp.entertainment}}</p>
                </div>
                <div class="card-details">
                    <p>Ngày gửi: {{ new Date(rsvp.timestamp).toLocaleString('vi-VN') }}</p>
                </div>
                <button @click="confirmDeleteRsvp(rsvp.id, rsvp.name)" class="delete-button">Xóa</button>
            </div>
        </div>

        <!-- Confirmation Modal -->
        <div v-if="showConfirmModal" class="modal-overlay">
            <div class="modal-content">
                <h2>Xác nhận xóa khách RSVP</h2>
                <p>Bạn có chắc chắn muốn xóa khách **{{ rsvpToDeleteName }}** không?</p>
                <div class="modal-actions">
                    <button @click="deleteRsvp" class="confirm-delete-button" :disabled="deleting">
                        <span v-if="deleting">Đang xóa...</span>
                        <span v-else>Xác nhận xóa</span>
                    </button>
                    <button @click="cancelDeleteRsvp" class="cancel-delete-button" :disabled="deleting">Hủy</button>
                </div>
                <p v-if="deleteError" class="delete-error-message">{{ deleteError }}</p>
            </div>
        </div>
    </div>
</template>

<script>
// Import các hàm cần thiết từ Firebase Realtime Database SDK
import { ref, onValue, remove } from 'firebase/database';

import ProgressSpinner from 'primevue/progressspinner';

export default {
    name: 'RsvpListComponent',
    components: {
        ProgressSpinner
    },
    data() {
        return {
            allRsvps: [], // Biến để lưu trữ dữ liệu RSVP
            loading: true, // Trạng thái tải dữ liệu
            error: null, // Thông báo lỗi nếu có
            unsubscribe: null, // Biến để lưu trữ hàm hủy đăng ký listener

            // Dữ liệu cho modal xác nhận xóa
            showConfirmModal: false,
            rsvpToDeleteId: null,
            rsvpToDeleteName: '',
            deleting: false, // Trạng thái đang xóa
            deleteError: null, // Lỗi khi xóa
        };
    },
    // Lifecycle hook: được gọi khi component được tạo
    created() {
        // Đảm bảo rằng $db đã được khởi tạo trước khi lắng nghe
        if (this.$db) {
            this.listenForRsvps();
        } else {
            console.error('Firebase Realtime Database chưa được khởi tạo. Vui lòng kiểm tra src/main.js');
            this.error = 'Lỗi khởi tạo cơ sở dữ liệu.';
            this.loading = false;
        }
    },
    // Lifecycle hook: được gọi ngay trước khi component bị hủy
    unmounted() {
        // Gỡ bỏ listener để tránh rò rỉ bộ nhớ
        if (this.unsubscribe) {
            this.unsubscribe();
            console.log("Đã hủy đăng ký listener RSVP.");
        }
    },
    computed: {
        attendingGuests() {
            // Trả về danh sách khách đang tham dự
            return this.allRsvps.filter(rsvp => rsvp.toCeremony || rsvp.toParty).reduce((acc, rsvp) => {
                acc.adultGuestsCount += rsvp.adultGuestsCount;
                acc.childGuestsCount += rsvp.childGuestsCount;
                return acc;
            }, {adultGuestsCount: 0, childGuestsCount: 0});
        },
    },
    methods: {
        isAttending(rsvp) {
            return rsvp.toCeremony || rsvp.toParty;
        },
        listenForRsvps() {
            this.loading = true;
            this.error = null;

            const rsvpsRef = ref(this.$db, 'rsvps'); // Lấy tham chiếu đến node 'rsvps'

            // onValue sẽ lắng nghe các thay đổi dữ liệu theo thời gian thực
            // và trả về một hàm hủy đăng ký (unsubscribe function)
            this.unsubscribe = onValue(rsvpsRef, (snapshot) => {
                if (snapshot.exists()) {
                    const rsvps = snapshot.val();

                    // Chuyển đổi đối tượng dữ liệu Firebase thành một mảng các đối tượng
                    // để dễ dàng duyệt và hiển thị trong template Vue
                    const rsvpsArray = Object.keys(rsvps).map(key => ({
                        id: key, // Khóa duy nhất do Firebase tạo
                        ...rsvps[key] // Dữ liệu RSVP thực tế
                    }));
                    this.allRsvps = rsvpsArray; // Cập nhật biến data của component

                } else {
                    console.log("Không tìm thấy dữ liệu RSVP nào (hoặc đã bị xóa).");
                    this.allRsvps = []; // Xóa danh sách nếu không có dữ liệu
                }
                this.loading = false; // Đã tải xong
            }, (error) => {
                console.error("Lỗi khi lắng nghe dữ liệu RSVP theo thời gian thực:", error);
                this.error = error.message; // Lưu thông báo lỗi
                this.loading = false; // Dừng trạng thái tải
            });
        },
        // Mở modal xác nhận xóa
        confirmDeleteRsvp(rsvpId, rsvpName) {
            this.rsvpToDeleteId = rsvpId;
            this.rsvpToDeleteName = rsvpName;
            this.showConfirmModal = true;
            this.deleteError = null; // Reset lỗi xóa
            this.deleting = false; // Reset trạng thái xóa
        },
        // Hủy thao tác xóa
        cancelDeleteRsvp() {
            this.showConfirmModal = false;
            this.rsvpToDeleteId = null;
            this.rsvpToDeleteName = '';
            this.deleteError = null;
        },
        // Thực hiện xóa dữ liệu khỏi Firebase
        async deleteRsvp() {
            if (!this.rsvpToDeleteId) return;

            this.deleting = true;
            this.deleteError = null;

            try {
                // Tạo tham chiếu đến mục cần xóa trong Firebase Realtime Database
                const itemRef = ref(this.$db, `rsvps/${this.rsvpToDeleteId}`);
                await remove(itemRef); // Xóa mục đó

                this.showConfirmModal = false; // Đóng modal
                this.rsvpToDeleteId = null;
                this.rsvpToDeleteName = '';
                console.log(`Đã xóa khách RSVP với ID: ${this.rsvpToDeleteId}`);
                // Dữ liệu sẽ tự động cập nhật nhờ listener onValue
            } catch (error) {
                console.error("Lỗi khi xóa khách RSVP:", error);
                this.deleteError = `Không thể xóa khách: ${error.message}`;
            } finally {
                this.deleting = false;
            }
        }
    }
};
</script>

<style scoped>
.rsvp-list-container {
    padding: 30px;
    max-width: 1200px;
    margin: 40px auto;
    background: #fff;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    font-family: Arial, sans-serif;
    color: #333;
}

.list-title {
    font-family: 'Dancing Script', cursive; /* Sử dụng font tương tự đám cưới */
    font-size: 3rem;
    color: #b23a48;
    text-align: center;
    margin-bottom: 30px;
}

.loading-message, .error-message, .no-data-message {
    text-align: center;
    font-size: 1.2rem;
    padding: 20px;
    border-radius: 8px;
    margin: 20px 0;
}

.loading-message {
    color: #007bff;
    background-color: #e9f5ff;
}

.error-message {
    color: #dc3545;
    background-color: #f8d7da;
}

.no-data-message {
    color: #6c757d;
    background-color: #f8f9fa;
}

.rsvp-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); /* Responsive grid */
    gap: 25px;
    justify-content: center;
}

.rsvp-card {
    background-color: #fefefe;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    position: relative; /* Cho button xóa */
}

.rsvp-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.card-name {
    font-family: 'Dancing Script', cursive;
    font-size: 2rem;
    color: #b23a48;
    margin-bottom: 10px;
    text-align: center;
}

.card-status {
    font-size: 1.1rem;
    font-weight: bold;
    text-align: center;
    padding: 5px 10px;
    border-radius: 5px;
    display: inline-block; /* Để các class màu có hiệu lực */
    margin-bottom: 10px;
    width: auto; /* Để chỉ chiếm vừa đủ chiều rộng */
    margin-left: auto;
    margin-right: auto;
}

.card-status.attending {
    color: #28a745;
    background-color: #d4edda;
}

.card-status.not-attending {
    color: #6c757d;
    background-color: #e2e3e5;
}

.card-guests {
    font-size: 1rem;
    color: #555;
    text-align: center;
    margin-bottom: 15px;
}

.card-details p {
    font-size: 0.95rem;
    color: #777;
    margin-bottom: 5px;
    text-align: center;
}

.card-details p:last-child {
    margin-bottom: 0;
}

/* --- DELETE BUTTON & MODAL STYLES --- */
.delete-button {
    background-color: #dc3545;
    color: white;
    border: none;
    padding: 8px 15px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.9rem;
    margin-top: 15px;
    display: block; /* Để button chiếm toàn bộ chiều rộng thẻ */
    width: 100%;
    transition: background-color 0.2s ease, transform 0.2s ease;
}

.delete-button:hover {
    background-color: #c82333;
    transform: translateY(-1px);
}

.delete-button:active {
    transform: translateY(0);
}

/* Modal Overlay */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5); /* Nền mờ */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000; /* Đảm bảo modal ở trên cùng */
}

.modal-content {
    background: white;
    padding: 30px;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    text-align: center;
    max-width: 400px;
    width: 90%;
}

.modal-content h2 {
    color: #b23a48;
    margin-bottom: 20px;
    font-size: 1.8rem;
}

.modal-content p {
    font-size: 1.1rem;
    margin-bottom: 25px;
    color: #555;
}

.modal-content p strong {
    color: #333;
}

.modal-actions button {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: bold;
    transition: background-color 0.2s ease;
    margin: 0 10px;
}

.confirm-delete-button {
    background-color: #dc3545;
    color: white;
}

.confirm-delete-button:hover:not(:disabled) {
    background-color: #c82333;
}

.confirm-delete-button:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
}

.cancel-delete-button {
    background-color: #f0f0f0;
    color: #333;
    border: 1px solid #ccc;
}

.cancel-delete-button:hover:not(:disabled) {
    background-color: #e0e0e0;
}

.cancel-delete-button:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
}

.delete-error-message {
    color: #dc3545;
    font-size: 0.9rem;
    margin-top: 15px;
}

/* Media queries for responsiveness */
@media (max-width: 768px) {
    .rsvp-list-container {
        padding: 20px;
        margin: 20px auto;
    }

    .list-title {
        font-size: 2.5rem;
        margin-bottom: 20px;
    }

    .rsvp-cards-grid {
        grid-template-columns: 1fr; /* Stack cards on very small screens */
    }

    .rsvp-card {
        padding: 15px;
    }

    .card-name {
        font-size: 1.8rem;
    }

    .card-status {
        font-size: 1rem;
    }

    .modal-content {
        padding: 20px;
    }

    .modal-content h2 {
        font-size: 1.5rem;
    }

    .modal-content p {
        font-size: 1rem;
    }

    .modal-actions button {
        padding: 8px 15px;
        font-size: 0.9rem;
        margin: 0 5px;
    }
}
</style>
